import { IconPrefix } from '@components/primitives/Icon/IconPrefix'
import { Text } from '@components/primitives/Text'
import { faAwardSimple } from '@fortawesome/pro-solid-svg-icons/faAwardSimple'
import { faBarsProgress } from '@fortawesome/pro-solid-svg-icons/faBarsProgress'
import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { faMapPin } from '@fortawesome/pro-solid-svg-icons/faMapPin'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import type { Geolocation } from '@lib/types/Geolocation'
import pluralize from '@lib/utilities/text/pluralize'
import { getTrialAttributes } from '@modules/trials/helpers/getTrialAttributes'
import { findClosestLocation } from '@modules/trials/helpers/parseLocationsFromTrial/parseTruncatedLocationsFromTrial'
import type { TrialForSummaryCard } from '@modules/trials/types/TrialForSummaryCard'

export default function TrialIconAttributeSection({
  nearestPoint,
  showLocation = true,
  trial,
}: {
  nearestPoint?: Geolocation
  showLocation?: boolean
  trial: TrialForSummaryCard
}) {
  const trialAttributes = getTrialAttributes(trial)

  const criteriaCount = trial.parsedEligibilityCriteria?.filter(
    (ie) => ie.isPreScreenerEnabled && ie.isInclusion,
  ).length

  const trialLocationsMonetized = trial.trialLocations
    .filter((tl) => tl.city && tl.state)
    .sort(
      (a, b) =>
        (b.managingTrialPros?.length ?? 0) - (a.managingTrialPros?.length ?? 0),
    )
  const closestTrialLocation =
    (nearestPoint
      ? findClosestLocation(trial.trialLocations, nearestPoint)?.location
      : trialLocationsMonetized[0]) ?? trial.trialLocations[0]

  const trialLocationText = [
    closestTrialLocation?.city,
    closestTrialLocation?.state,
  ]
    .filter((v) => typeof v === 'string')
    .join(', ')

  const showPhaseBadge =
    trialAttributes.Phase !== 'N/A' && trialAttributes.Phase !== undefined
  const showCriteriaBadge = !showPhaseBadge && criteriaCount > 0
  return (
    <>
      <div className='flex flex-row items-center gap-x-2'>
        <IconPrefix className='text-primary400' icon={faUser} iconSize='sm'>
          <Text className='text-black' styleName='p-small' value='Recruiting' />
        </IconPrefix>
        <IconPrefix className='text-hope600' icon={faAwardSimple} iconSize='sm'>
          <Text
            className='text-black'
            styleName='p-small'
            value={pluralize('award', trial.awardBadges.length, {
              includeCount: true,
            })}
          />
        </IconPrefix>
        {showPhaseBadge && (
          <IconPrefix
            className='text-turquoise500'
            icon={faBarsProgress}
            iconSize='sm'
          >
            <Text
              className='text-black'
              styleName='p-small'
              value={trialAttributes.Phase}
            />
          </IconPrefix>
        )}
        {showCriteriaBadge && (
          <IconPrefix className='text-success500' icon={faList} iconSize='sm'>
            <Text
              className='text-black'
              styleName='p-small'
              value={`${criteriaCount} criteria`}
            />
          </IconPrefix>
        )}
      </div>
      {showLocation && (
        <div className='flex flex-row items-center gap-x-2'>
          {trialLocationText.length > 0 && (
            <IconPrefix className='text-blue500' icon={faMapPin} iconSize='sm'>
              <Text
                className='text-black'
                styleName='p-small'
                value={trialLocationText}
              />
            </IconPrefix>
          )}
        </div>
      )}
    </>
  )
}
